import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { savePrefs } from '../../auth/passwordSession';
import { useViewerGlide } from '../../viewer/glide';
import { useSession } from '../../auth/useSession';
import { toast } from '../../community/ui/toast';

type SessionLike = { userId?: string | null; prefs?: Record<string, unknown> } | null | undefined;

/**
 * [glide, setGlide]: whether the image keeps moving after a drag or scroll is
 * let go. Signed in, the choice is an account preference (`viewerGlide`) that
 * follows the person between devices, as the viewer theme does; the viewer
 * itself reads this browser's copy, kept in step here.
 */
export function useViewerGlideSetting(): [boolean, (glide: boolean) => void] {
  const { data: me } = useSession();
  const qc = useQueryClient();
  const local = useViewerGlide((s) => s.glide);
  const setLocal = useViewerGlide((s) => s.set);
  const pref = me?.prefs?.viewerGlide;
  const glide = typeof pref === 'boolean' ? pref : local;
  useEffect(() => {
    if (typeof pref === 'boolean' && pref !== local) setLocal(pref);
  }, [pref, local, setLocal]);
  const set = (v: boolean) => {
    setLocal(v);
    if (!me?.userId) return;
    qc.setQueryData<SessionLike>(['session'], (old) => (old ? { ...old, prefs: { ...(old.prefs ?? {}), viewerGlide: v } } : old));
    savePrefs({ viewerGlide: v }).catch(() => {
      toast('Your setting could not be saved');
      void qc.invalidateQueries({ queryKey: ['session'] });
    });
  };
  return [glide, set];
}
